/**
 * Keeps a photo filling its frame slot while a guest pans or pinch-zooms it on the
 * Select preview (PrintPreview).
 *
 * Why (2026-10-03, Memoria session jpgqzt): the preview let a pinch take a photo down
 * to 0.1x, far below the size that fills its slot. Once the photo was smaller than the
 * slot, the old offset clamp `max(-m, min(m, v))` had a negative `m` and always returned
 * +|slot - photo|, so the photo was shoved down/right and the frame's white background
 * printed as a band across the top of the slot. A separate 2x "overpan" allowance let a
 * zoomed-in photo be dragged past its own edge, which exposed the background the same way.
 *
 * Offsets are measured from the slot centre in the slot's own (rotated) frame, which is
 * how PrintPreview draws them: translate to the centre, rotate, clip a width x height
 * aperture, then draw the photo at -size/2 + offset.
 */

export const MIN_CUTOUT_SCALE = 0.1
export const MAX_SCALE = 5

export interface SlotFitInput {
  naturalWidth: number
  naturalHeight: number
  slotWidth: number
  slotHeight: number
  scale: number
  offsetX: number
  offsetY: number
  /**
   * Background-removed slots draw a cut-out whose surroundings are transparent anyway, so a
   * guest may still shrink it below the slot. It stays inside the slot instead of covering it.
   */
  allowBelowCover?: boolean
}

export interface SlotFit {
  scale: number
  offsetX: number
  offsetY: number
}

/** Smallest scale at which the photo covers the whole slot. */
export function coverScale(
  naturalWidth: number,
  naturalHeight: number,
  slotWidth: number,
  slotHeight: number
): number {
  return Math.max(slotWidth / naturalWidth, slotHeight / naturalHeight)
}

export function fitPhotoToSlot(input: SlotFitInput): SlotFit {
  const { naturalWidth, naturalHeight, slotWidth, slotHeight } = input
  if (!(naturalWidth > 0 && naturalHeight > 0 && slotWidth > 0 && slotHeight > 0)) {
    return { scale: input.scale, offsetX: input.offsetX, offsetY: input.offsetY }
  }

  const floor = input.allowBelowCover
    ? MIN_CUTOUT_SCALE
    : coverScale(naturalWidth, naturalHeight, slotWidth, slotHeight)
  // The floor wins over MAX_SCALE: a tiny photo in a large slot must still cover it.
  const scale = Math.max(floor, Math.min(MAX_SCALE, input.scale))

  // Larger than the slot: never pan past the photo's edge. Smaller (cut-outs only): stay inside.
  const slackX = Math.abs(naturalWidth * scale - slotWidth) / 2
  const slackY = Math.abs(naturalHeight * scale - slotHeight) / 2

  return {
    scale,
    offsetX: Math.max(-slackX, Math.min(slackX, input.offsetX)),
    offsetY: Math.max(-slackY, Math.min(slackY, input.offsetY))
  }
}
